import axios from 'axios'
import { type ApiError } from '@/types/api'
import { type LoginRequest, type LoginResponse, type AuthCheckResponse } from '@/types/auth'
import { type Recipe, type RecipeListItem, type RecipeListParams, type RecipeImage, type ResolvedVideo } from '@/types/recipe'
import { type Pagination } from '@/types/api'
import { type Category, type CreateCategoryRequest, type UpdateCategoryRequest, type ReorderCategoriesRequest } from '@/types/category'
import { type Tag, type CreateTagRequest } from '@/types/tag'
import { type Ingredient, type CreateIngredientRequest } from '@/types/ingredient'

export class ApiRequestError extends Error {
	status: number
	code: string
	details: Array<{ field: string; message: string }> | undefined

	constructor(
		status: number,
		code: string,
		message: string,
		details?: Array<{ field: string; message: string }>,
	) {
		super(message)
		this.name = 'ApiRequestError'
		this.status = status
		this.code = code
		this.details = details
	}
}

export const api = axios.create({
	baseURL: '/api/v1',
	withCredentials: true,
})

api.interceptors.response.use(
	(response) => response,
	(error) => {
		if (axios.isAxiosError(error) && error.response?.data) {
			const { status, data } = error.response
			const apiError = data as ApiError
			throw new ApiRequestError(
				status,
				apiError.code ?? 'UNKNOWN_ERROR',
				apiError.message ?? 'Произошла ошибка',
				apiError.details,
			)
		}
		throw error
	},
)

export const authService = {
	login: async (data: LoginRequest): Promise<LoginResponse> => {
		const res = await api.post<LoginResponse>('/auth/login', data)
		return res.data
	},

	logout: async (): Promise<void> => {
		await api.post('/auth/logout')
	},

	check: async (): Promise<AuthCheckResponse> => {
		const res = await api.get<AuthCheckResponse>('/auth/check')
		return res.data
	},
}

export const recipeService = {
	list: async (params?: RecipeListParams): Promise<{ recipes: RecipeListItem[]; pagination: Pagination }> => {
		const res = await api.get<{ recipes: RecipeListItem[]; pagination: Pagination }>('/recipes', { params })
		return res.data
	},

	get: async (id: string): Promise<Recipe> => {
		const res = await api.get<{ recipe: Recipe }>(`/recipes/${id}`)
		return res.data.recipe
	},

	create: async (data: Omit<Recipe, 'id' | 'created_at' | 'updated_at'>): Promise<Recipe> => {
		const res = await api.post<{ recipe: Recipe }>('/recipes', data)
		return res.data.recipe
	},

	update: async (id: string, data: Omit<Recipe, 'id' | 'created_at' | 'updated_at'>): Promise<Recipe> => {
		const res = await api.put<{ recipe: Recipe }>(`/recipes/${id}`, data)
		return res.data.recipe
	},

	delete: async (id: string): Promise<void> => {
		await api.delete(`/recipes/${id}`)
	},
}

export const imageService = {
	list: async (recipeId: string): Promise<RecipeImage[]> => {
		const res = await api.get<{ images: RecipeImage[] }>(`/recipes/${recipeId}/images`)
		return res.data.images
	},

	upload: async (
		recipeId: string,
		file: File,
		onProgress?: (pct: number) => void,
	): Promise<RecipeImage> => {
		const formData = new FormData()
		formData.append('image', file)
		const config: Record<string, unknown> = {
			headers: { 'Content-Type': 'multipart/form-data' },
		}
		if (onProgress) {
			config.onUploadProgress = (evt: { loaded: number; total?: number }) => {
				if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100))
			}
		}
		const res = await api.post<{ image: RecipeImage }>(
			`/recipes/${recipeId}/images`,
			formData,
			config,
		)
		return res.data.image
	},

	reorder: async (recipeId: string, order: string[]): Promise<void> => {
		await api.put(`/recipes/${recipeId}/images/reorder`, { order })
	},

	delete: async (recipeId: string, imageId: string): Promise<void> => {
		await api.delete(`/recipes/${recipeId}/images/${imageId}`)
	},

	fileUrl: (recipeId: string, imageId: string): string => {
		return `/api/v1/recipes/${recipeId}/images/${imageId}/file`
	},
}

export const categoryService = {
	list: async (): Promise<Category[]> => {
		const res = await api.get<{ categories: Category[] }>('/categories')
		return res.data.categories
	},

	get: async (id: string): Promise<Category> => {
		const res = await api.get<{ category: Category }>(`/categories/${id}`)
		return res.data.category
	},

	create: async (data: CreateCategoryRequest): Promise<Category> => {
		const res = await api.post<{ category: Category }>('/categories', data)
		return res.data.category
	},

	update: async (id: string, data: UpdateCategoryRequest): Promise<Category> => {
		const res = await api.put<{ category: Category }>(`/categories/${id}`, data)
		return res.data.category
	},

	reorder: async (data: ReorderCategoriesRequest): Promise<void> => {
		await api.put('/categories/reorder', data)
	},

	delete: async (id: string): Promise<void> => {
		await api.delete(`/categories/${id}`)
	},
}

export const tagService = {
	search: async (query: string): Promise<Tag[]> => {
		const res = await api.get<{ tags: Tag[] }>('/tags', { params: { q: query } })
		return res.data.tags
	},

	create: async (data: CreateTagRequest): Promise<Tag> => {
		const res = await api.post<{ tag: Tag }>('/tags', data)
		return res.data.tag
	},
}

export const ingredientService = {
	search: async (query: string): Promise<Ingredient[]> => {
		const res = await api.get<{ ingredients: Ingredient[] }>('/ingredients', { params: { q: query } })
		return res.data.ingredients
	},

	create: async (data: CreateIngredientRequest): Promise<Ingredient> => {
		const res = await api.post<{ ingredient: Ingredient }>('/ingredients', data)
		return res.data.ingredient
	},
}

export const onedriveService = {
	resolveVideos: async (url: string): Promise<ResolvedVideo[]> => {
		const res = await api.get<{ videos: ResolvedVideo[] }>('/onedrive/resolve', {
			params: { url },
		})
		return res.data.videos
	},
}

export const chatService = {
	streamChat: async (
	  message: string,
	  recipe: Record<string, unknown>,
	  history: Array<{ role: string; content: string }>,
	  onToken: (token: string) => void,
	  onError: (error: string) => void,
	  onDone: () => void,
	  onSearch?: (query: string) => void,
	): Promise<void> => {
		// Step 1: Make the fetch request
		const response = await fetch('/api/v1/chat', {
			method: 'POST',
			credentials: 'include',  // sends cookies, same as withCredentials
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ message, recipe, history }),
		})

		// Step 2: Check for HTTP errors (non-200)
		if (!response.ok) {
			onError('Chat request failed')
			return
		}

		// Step 3: Get a reader from the response body stream
		const reader = response.body!.getReader()
		const decoder = new TextDecoder()

		// Step 4: Buffer for incomplete SSE events
		let buffer = ''

		// Step 5: Read loop
		while (true) {
			const { done, value } = await reader.read()
			if (done) break

			// Decode the chunk and add to buffer
			buffer += decoder.decode(value, { stream: true })

			// Step 6: Split buffer on double newlines (SSE event boundary)
			const events = buffer.split('\n\n')

			// Last element might be incomplete — keep it in buffer
			buffer = events.pop() ?? ''

			// Step 7: Process each complete event
			for (const event of events) {
				if (!event.trim()) continue

				// Parse "event: <type>" and "data: <json>" lines
				let eventType = ''
				let eventData = ''

				for (const line of event.split('\n')) {
					if (line.startsWith('event: ')) {
						eventType = line.slice(7)
					} else if (line.startsWith('data: ')) {
						eventData = line.slice(6)
					}
				}

				// Step 8: Handle each event type
				if (eventType === 'token') {
					const parsed = JSON.parse(eventData)
					onToken(parsed.content)
				} else if (eventType === 'error') {
					const parsed = JSON.parse(eventData)
					onError(parsed.message)
					return
				} else if (eventType === 'search') {
					if (onSearch) {
						const parsed = JSON.parse(eventData)
						onSearch(parsed.query)
					}
				} else if (eventType === 'done') {
					onDone()
					return
				}
			}
		}
	}
}